import { ReactElement, useMemo, useState } from 'react';
import {
  ColorInput,
  FileToUploadInputType,
  FileUploadProgress,
  FullLoader,
  IconButton,
  Input,
  Modal,
  ModalFooter,
  RadioGroup,
  UploadButton,
  UploadedFile,
} from '@sc/shared-ui';
import {
  InformationCircleIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery } from '@apollo/client/react';
import { useForm } from '@tanstack/react-form';
import { getFragmentData, ThemeFragment, ThemeType } from '@sc/graphql';
import {
  GQL_FRAGMENT_THEME,
  GQL_MUTATION_CREATE_THEME,
  GQL_MUTATION_UPDATE_THEME,
  GQL_QUERY_INDEX_THEMES,
  GQL_QUERY_READ_THEME,
} from '../utility/queries';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';

const BRAND_COLOUR_PATTERN = /^#[0-9a-fA-F]{6}$/;

const changedThemeFields = ({
  existing,
  value,
}: {
  existing: ThemeFragment | null;
  value: {
    brandColour: string;
    logo: { uuid: string } | undefined;
    name: string;
    subtitle: string;
    themeType: ThemeType;
    title: string;
  };
}): string[] => {
  if (!existing) return [];

  const comparisons: Record<string, boolean> = {
    brandColour: value.brandColour !== existing.brandColour,
    logo: value.logo?.uuid !== existing.logo.uuid,
    name: value.name !== existing.name,
    subtitle: value.subtitle !== (existing.subtitle ?? ''),
    themeType: value.themeType !== existing.themeType,
    title: value.title !== (existing.title ?? ''),
  };

  return Object.entries(comparisons)
    .filter(([, hasChanged]) => hasChanged)
    .map(([field]) => field);
};

interface Props {
  open: boolean;
  mode: 'add' | 'edit';
  uuid: string | undefined;
  onClose: () => void;
}

export const ThemeFormModal = ({
  open,
  mode,
  uuid,
  onClose,
}: Props): ReactElement => {
  const { useApolloError } = useAlerts();
  const onError = useApolloError();

  const { data: readData, loading: readLoading } = useQuery(
    GQL_QUERY_READ_THEME,
    {
      variables: { uuid: uuid ?? '' },
      skip: mode !== 'edit' || !uuid || !open,
      fetchPolicy: 'cache-and-network',
    }
  );

  const existing = useMemo(
    () =>
      readData?.readTheme
        ? getFragmentData(GQL_FRAGMENT_THEME, readData.readTheme)
        : null,
    [readData]
  );

  const [createTheme, { loading: creating }] = useMutation(
    GQL_MUTATION_CREATE_THEME
  );

  const [updateTheme, { loading: updating }] = useMutation(
    GQL_MUTATION_UPDATE_THEME
  );

  const [uploadingFiles, setUploadingFiles] = useState<FileToUploadInputType[]>(
    []
  );

  const isUploading = uploadingFiles.length > 0;

  const isModalLoading =
    creating || updating || (mode === 'edit' && readLoading);

  const isSubmitDisabled = isModalLoading || isUploading;

  const form = useForm({
    defaultValues: {
      name: existing?.name ?? '',
      logo: existing?.logo ?? undefined,
      themeType: existing?.themeType ?? ThemeType.dark,
      brandColour: existing?.brandColour ?? '',
      title: existing?.title ?? '',
      subtitle: existing?.subtitle ?? '',
    },
    onSubmit: ({ value, formApi }) => {
      if (!value.logo) return;

      if (mode === 'add') {
        return void createTheme({
          variables: {
            input: {
              name: value.name,
              themeType: value.themeType,
              brandColour: value.brandColour,
              title: value.title || null,
              subtitle: value.subtitle || null,
              logoUuid: value.logo.uuid,
            },
          },
          refetchQueries: [GQL_QUERY_INDEX_THEMES],
          onCompleted: (data) => {
            notify.success('Successfully created theme');

            track('Theme Created', { themeUuid: data.createTheme.uuid });

            void formApi.reset(); 

            onClose();
          },
          onError,
        });
      }

      if (!uuid) return;

      const fieldsChanged = changedThemeFields({ existing, value });

      return void updateTheme({
        variables: {
          input: {
            uuid,
            name: value.name,
            themeType: value.themeType,
            brandColour: value.brandColour,
            title: value.title || null,
            subtitle: value.subtitle || null,
            logoUuid: value.logo.uuid,
          },
        },
        refetchQueries: [GQL_QUERY_INDEX_THEMES],
        update: (cache) => {
          cache.modify({
            id: `ProjectTheme:${uuid}`,
            fields: {
              name: () => value.name,
              brandColour: () => value.brandColour,
              themeType: () => value.themeType,
              title: () => value.title || null,
              subtitle: () => value.subtitle || null,
              logoSrc: (_, { INVALIDATE }) => INVALIDATE,
            },
          });
        },
        onCompleted: () => {
          notify.success('Successfully updated theme');

          track('Theme Updated', { fieldsChanged, themeUuid: uuid });

          void formApi.reset();

          onClose();
        },
        onError,
      });
    },
  });

  return (
    <Modal
      title={mode === 'add' ? 'Add theme' : 'Edit theme'}
      open={open}
      loading={isModalLoading}
      onClose={() => {
        form.reset(); 

        onClose(); 
      }}
      dialogPanelClassname="w-[480px]"
    >
      {mode === 'edit' && readLoading && !existing ? (
        <div className="p-10 flex items-center justify-center">
          <FullLoader className="size-8" />
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();

            void form.handleSubmit();
          }}
        >
          <div className="p-5 space-y-5">
            <form.Field
              name="name"
              children={({ state, handleChange }) => (
                <Input
                  label="Name"
                  helperText="Usually the brand name or company name"
                  value={state.value}
                  required
                  error={state.meta.errors.filter(Boolean).join(', ')}
                  onChange={(e) => handleChange(e.target.value)}
                />
              )}
            />

            <form.Field
              name="title"
              children={({ state, handleChange }) => (
                <Input
                  label="Chat title"
                  helperText="Shown above the chat input. Replaces “Ask me anything” when set"
                  value={state.value}
                  error={state.meta.errors.filter(Boolean).join(', ')}
                  onChange={(e) => handleChange(e.target.value)}
                />
              )}
            />

            <form.Field
              name="subtitle"
              children={({ state, handleChange }) => (
                <Input
                  label="Chat subtitle"
                  helperText="Shown beneath the title. Replaces the default ready-to-use message when set"
                  value={state.value}
                  error={state.meta.errors.filter(Boolean).join(', ')}
                  onChange={(e) => handleChange(e.target.value)}
                />
              )}
            />

            <form.Field
              name="logo"
              validators={{
                onSubmit: ({ value }) =>
                  value ? undefined : 'Logo is required',
              }}
              children={({ state, handleChange }) => {
                const hasError = state.meta.errors.filter(Boolean).length > 0;

                return (
                  <div className="space-y-2">
                    <span className="block font-semibold text-body-medium">
                      Upload logo
                    </span>
                    <div className="flex items-center space-x-3">
                      {state.value && (
                        <div className="flex items-center space-x-2 border border-grey-t-700 rounded-xs px-3 h-11 bg-background-secondary max-w-xs grow">
                          <PhotoIcon className="size-5 text-grey-400 shrink-0" />
                          <span className="truncate grow text-sm">
                            {state.value.name}
                          </span>
                          <IconButton
                            Icon={XMarkIcon}
                            size="small"
                            type="basic"
                            onClick={() => handleChange(undefined)}
                          />
                        </div>
                      )}
                      <UploadButton
                        keyPrefix="themes"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        fileLimit={1}
                        label="Select"
                        uploading={uploadingFiles}
                        setUploading={setUploadingFiles}
                        onFilesUpload={(files: UploadedFile[]) => {
                          const first = files[0];
                          if (first) {
                            handleChange({
                              uuid: first.uuid,
                              name: first.name,
                              src: first.src,
                            });
                          }
                        }}
                      />
                    </div>
                    <FileUploadProgress
                      uploading={uploadingFiles}
                      Icon={PhotoIcon}
                    />
                    {hasError && (
                      <div className="flex space-x-1.5 items-start mt-2">
                        <InformationCircleIcon className="shrink-0 size-4 text-red-300 mt-[3px]" />
                        <span className="text-body-smallest text-red-300">
                          {state.meta.errors.filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                );
              }}
            />

            <form.Field
              name="themeType"
              children={({ state, handleChange }) => (
                <div className="space-y-1">
                  <span className="block font-semibold text-body-medium">
                    Theme Type
                  </span>
                  <span className="block text-text-low-priority text-sm">
                    Choose a theme type that matches your website. Light is a
                    typical website with a white background. Dark is the inverse
                    of that, typically black or almost black background.
                  </span>
                  <RadioGroup<ThemeType>
                    options={[
                      { label: 'Light', value: ThemeType.light },
                      { label: 'Dark', value: ThemeType.dark },
                    ]}
                    selected={state.value}
                    setSelected={handleChange}
                    error={state.meta.errors.filter(Boolean).join(', ')}
                  />
                </div>
              )}
            />

            <form.Field
              name="brandColour"
              validators={{
                onChange: ({ value }) =>
                  value.length === 0 || BRAND_COLOUR_PATTERN.test(value)
                    ? undefined
                    : 'Enter a valid hex colour (e.g. #274ECF)',
                onSubmit: ({ value }) =>
                  BRAND_COLOUR_PATTERN.test(value)
                    ? undefined
                    : 'Enter a valid hex colour (e.g. #274ECF)',
              }}
              children={({ state, handleChange }) => (
                <ColorInput
                  label="Accent colour"
                  helperText="This colour will be used in buttons and clickable links"
                  value={state.value}
                  required
                  error={
                    state.meta.errors.filter(Boolean).join(', ') || undefined
                  }
                  onChange={(e) => handleChange(e.target.value)}
                />
              )}
            />
          </div>
          <ModalFooter
            asForm
            buttonsDisabled={isSubmitDisabled}
            onClose={onClose}
            submitText={mode === 'add' ? 'Save & add' : 'Save'}
          />
        </form>
      )}
    </Modal>
  );
};
